<template>
  <modal :show.sync="show" :on-close="closeModal">
    <div v-if="hasSteps" class="stepper">
      <div class="stepper-track">
        <div class="stepper-progress" :style="{ width: progressWidth }"></div>
      </div>

      <div class="stepper-items">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="step"
          :class="{
            active: index === currentStepIndex,
            completed: index < currentStepIndex,
          }"
        >
          {{ index + 1 }}
        </div>
      </div>
    </div>

    <woot-modal-header :header-title="hasSteps ? currentStep.title : title" />

    <div
      class="custom-message"
      v-dompurify-html="hasSteps ? currentStep.message : message"
    />

    <form class="form-container" @submit.prevent="handleProceed">
      <woot-input
        v-if="shouldShowInput"
        v-model="value"
        type="text"
        :class="{ error: $v.value.$error }"
        :placeholder="
          currentStep.confirmPlaceHolderText || confirmPlaceHolderText
        "
        @blur="$v.value.$touch"
      />

      <div v-if="shouldShowButtons" class="button-wrapper">
        <template v-if="hasSteps">
          <woot-button class="primary-button" @click.prevent="closeModal">
            Manter a caixa de entrada
          </woot-button>

          <button
            class="danger-button"
            :disabled="isConfirmDisabled"
            @click.prevent="handleProceed"
          >
            {{ isLastStep ? 'Excluir' : 'Prosseguir' }}
          </button>
        </template>

        <template v-else>
          <woot-button color-scheme="alert" :is-disabled="isConfirmDisabled">
            {{ confirmText }}
          </woot-button>

          <woot-button
            v-if="hasRejectButton"
            class="clear"
            @click.prevent="closeModal"
          >
            {{ rejectText }}
          </woot-button>
        </template>
      </div>
    </form>
  </modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import Modal from '../../Modal';

export default {
  components: {
    Modal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    confirmText: {
      type: String,
      default: '',
    },
    rejectText: {
      type: String,
      default: '',
    },
    confirmValue: {
      type: String,
      default: '',
    },
    confirmPlaceHolderText: {
      type: String,
      default: '',
    },
    showInput: {
      type: Boolean,
      default: true,
    },
    steps: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      value: '',
      currentStepIndex: 0,
    };
  },
  computed: {
    hasSteps() {
      return this.steps && this.steps.length > 0;
    },
    currentStep() {
      return this.hasSteps ? this.steps[this.currentStepIndex] : {};
    },
    shouldShowInput() {
      if (this.hasSteps && this.currentStep.showInput !== undefined) {
        return this.currentStep.showInput;
      }
      return this.showInput;
    },
    isConfirmDisabled() {
      if (!this.shouldShowInput) return false;
      return this.$v.value.$invalid;
    },
    hasRejectButton() {
      return this.currentStep.rejectText || this.rejectText;
    },
    shouldShowButtons() {
      return this.hasSteps || this.confirmText || this.hasRejectButton;
    },
    progressWidth() {
      if (!this.hasSteps) return '0%';
      const percentage =
        (this.currentStepIndex / (this.steps.length - 1)) * 100;
      return `${percentage}%`;
    },
    isLastStep() {
      return this.hasSteps && this.currentStepIndex === this.steps.length - 1;
    },
  },
  validations() {
    if (!this.shouldShowInput) {
      return {};
    }
    return {
      value: {
        required,
        isEqual(value) {
          return value === (this.currentStep.confirmValue || this.confirmValue);
        },
      },
    };
  },
  methods: {
    closeModal() {
      this.value = '';
      this.currentStepIndex = 0;
      this.$emit('on-close');
    },
    handleProceed() {
      if (this.shouldShowInput && this.isConfirmDisabled) return;

      if (this.hasSteps && this.currentStepIndex < this.steps.length - 1) {
        this.currentStepIndex += 1;
        this.value = '';
        return;
      }

      this.$emit('on-confirm');
      this.closeModal();
    },
  },
};
</script>

<style scoped>
.stepper {
  position: relative;
  width: 60%;
  margin: 40px auto 16px;
}

.stepper-track {
  position: absolute;
  top: 26px;
  left: 40px;
  right: 40px;
  height: 4px;
  background: #e5e7eb;
  border-radius: 999px;
}

.stepper-progress {
  height: 100%;
  background: #22c55e;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.stepper-items {
  display: flex;
  justify-content: space-between;
  position: relative;
}

.step {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  color: #6b7280;
  z-index: 1;
}

.step.active {
  background: #2563eb;
  color: white;
}

.step.completed {
  background: #22c55e;
  color: white;
}

:deep(.woot-modal-header) {
  padding-top: 8px;
  padding-bottom: 8px;
  text-align: left;
  padding-left: 24px;
}

:deep(.woot-modal-header__title) {
  font-size: 22px;
  font-weight: 700;
  text-align: left;
}

.custom-message {
  max-width: 520px;
  margin: 16px 0 0 24px;
  text-align: left;
  font-size: 14px;
  line-height: 1.6;
  color: #1f2937;
}

.form-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 24px;
}

:deep(.woot-input) {
  width: 100%;
  max-width: 520px;
  margin-top: 20px;
}

:deep(.woot-input input) {
  width: 100%;
}

:deep(.woot-input__input),
:deep(.woot-input__wrapper) {
  width: 100%;
}

.button-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
  padding-bottom: 12px;
}

.button-wrapper.no-input {
  margin-top: 12px;
}

.primary-button {
  background: #2563eb;
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
}

.danger-button {
  background: transparent;
  border: none;
  color: #ef4444;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
}
</style>
